import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toLocalISODate } from "../../lib/dates";
import { toast } from "sonner";
import { Users, DollarSign, CalendarCheck, UserPlus, HeartHandshake, MessageSquare, Trophy, FileDown, Lock } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { formatError } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { downloadPdf } from "../../components/FileUpload";
import { Button } from "../../components/ui/button";

const CHART_MARGIN = { left: -10, right: 10 };
const TICK = { fontSize: 11 };

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Metric({ icon: Icon, label, value, sub, color = "text-crimson", testId, to }) {
  const body = (<>
    <div className="flex items-center justify-between"><p className="eyebrow text-gold">{label}</p><Icon size={18} className={color} /></div>
    <p className={`mt-2 font-mono-accent text-3xl font-bold ${color}`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    {to && <p className="mt-1 text-[11px] font-semibold text-palm opacity-70 group-hover:opacity-100">View details →</p>}
  </>);
  if (!to) return <div className="glass rounded-2xl p-5" data-testid={testId}>{body}</div>;
  return <Link to={to} className="group glass block rounded-2xl p-5 transition-transform hover:-translate-y-0.5 hover:ring-2 hover:ring-gold/60" data-testid={testId}>{body}</Link>;
}

export function OverviewMetrics() {
  const { data: d } = useApi("/admin/overview");
  if (!d) return <p className="text-muted-foreground">Loading metrics…</p>;
  const methods = Object.entries(d.income_by_method).sort((a, b) => b[1] - a[1]);
  const methodTotal = methods.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="space-y-6" data-testid="overview-metrics">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Metric icon={Users} label="Good standing" value={`${d.members.good_pct}%`} sub={`${d.members.good_standing} of ${d.members.total} members · ${d.members.arrears} in arrears`} color="text-palm" testId="m-good-standing" to="/admin/members?q=balance%3A%3E0" />
        <Metric icon={DollarSign} label="Collection rate" value={`${d.dues.collection_rate}%`} sub={`${money(d.dues.collected_ytd)} collected · ${money(d.dues.receivable)} receivable`} testId="m-collection" to="/admin/dues" />
        <Metric icon={CalendarCheck} label="Avg attendance" value={`${d.attendance.avg_pct}%`} sub={`${d.attendance.meetings_held} meetings held this year`} color="text-gold" testId="m-attendance" to="/admin/meetings" />
        <Metric icon={UserPlus} label="Awaiting accounts" value={d.registrations_pending} sub={`${d.dues.pending_offline_claims} offline claims to confirm`} testId="m-registrations" to="/admin/members?filter=pending" />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="glass rounded-2xl p-6 lg:col-span-2">
          <h3 className="font-serif-display text-xl font-bold text-crimson">Income — last 12 months</h3>
          <div className="mt-4 h-56" data-testid="income-trend">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.trend} margin={CHART_MARGIN}>
                <defs><linearGradient id="inc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7A0C0C" stopOpacity={0.5} /><stop offset="100%" stopColor="#7A0C0C" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#D99B2633" />
                <XAxis dataKey="month" tick={TICK} tickFormatter={(m) => m.slice(5)} />
                <YAxis tick={TICK} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => money(v)} />
                <Area type="monotone" dataKey="amount" stroke="#7A0C0C" strokeWidth={2} fill="url(#inc)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif-display text-xl font-bold text-crimson">Income by method (YTD)</h3>
          <div className="mt-4 space-y-3" data-testid="income-methods">
            {methods.map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm"><span className="capitalize">{k}</span><span className="font-mono-accent">{money(v)}</span></div>
                <div className="mt-1 h-2 rounded-full bg-parchment"><div className="h-2 rounded-full bg-gold" style={{ width: `${(v / methodTotal) * 100}%` }} /></div>
              </div>
            ))}
            {methods.length === 0 && <p className="text-sm text-muted-foreground">No income recorded this year.</p>}
          </div>
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2"><HeartHandshake size={18} className="text-palm" /><h3 className="font-serif-display text-xl font-bold text-crimson">Welfare</h3></div>
          <p className="mt-2 font-mono-accent text-2xl font-bold text-palm">{money(d.welfare.disbursed)} <span className="text-xs font-normal text-muted-foreground">disbursed</span></p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="welfare-status">
            {Object.entries(d.welfare.by_status).map(([s, n]) => <span key={s} className="rounded-full bg-crimson/10 px-2 py-0.5 text-xs text-crimson">{s.replace(/_/g, " ")}: {n}</span>)}
            {Object.keys(d.welfare.by_status).length === 0 && <span className="text-sm text-muted-foreground">No claims yet.</span>}
          </div>
        </div>
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif-display text-xl font-bold text-crimson">Upcoming events</h3>
          <ul className="mt-3 space-y-2 text-sm" data-testid="upcoming-events">
            {d.upcoming.map((e) => <li key={e.id} className="flex justify-between"><span>{e.date} · {e.title}</span><span className="font-mono-accent text-palm">{e.going} going</span></li>)}
            {d.upcoming.length === 0 && <li className="text-muted-foreground">Nothing scheduled.</li>}
          </ul>
        </div>
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2"><MessageSquare size={18} className="text-gold" /><h3 className="font-serif-display text-xl font-bold text-crimson">Reminders & SMS</h3></div>
          <p className="mt-2 text-xs" data-testid="sms-status">
            SMS: {d.sms.configured ? <span className="text-palm font-semibold">configured</span> : <span className="text-amber-700 font-semibold">not configured (add Twilio keys)</span>} · {d.sms.sent} sent · {d.sms.queued} skipped
          </p>
          <ul className="mt-3 space-y-1 text-xs text-muted-foreground" data-testid="cron-runs">
            {d.cron_runs.map((r) => <li key={r.id}>{toLocalISODate(r.started_at)} · {r.job} · {r.status} · {r.reminders_sent} sent</li>)}
            {d.cron_runs.length === 0 && <li>No automated runs yet.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function ElectionResults({ election, isOfficer, onChanged }) {
  const { data, reload } = useApi(`/elections/${election.id}/tally`);
  const [busy, setBusy] = useState(false);
  const act = async (path, msg) => {
    setBusy(true);
    try { await api.post(`/admin/elections/${election.id}/${path}`); toast.success(msg); reload(); onChanged?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!data) return isOfficer ? null : (
    <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground" data-testid={`results-locked-${election.id}`}><Lock size={12} /> Results are published after voting closes.</p>
  );
  const max = Math.max(1, ...data.results.map((r) => r.votes));
  return (
    <div className="mt-4 rounded-xl border border-gold/30 bg-parchment-card p-4" data-testid={`results-${election.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-semibold text-crimson">{data.live ? "Live tally" : "Final results"} <span className="text-xs font-normal text-muted-foreground">· {data.total_votes} ballots · {data.turnout_pct}% turnout</span></h4>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => downloadPdf(`/elections/${election.id}/results.pdf`, `results-${election.position}.pdf`)} data-testid={`results-pdf-${election.id}`}><FileDown size={14} className="mr-1" /> PDF</Button>
          {isOfficer && data.live && <Button size="sm" variant="outline" disabled={busy} onClick={() => act("close", "Voting closed")} data-testid={`close-${election.id}`}>Close voting</Button>}
          {isOfficer && data.closed && !data.winner && <Button size="sm" disabled={busy} className="bg-palm" onClick={() => act("declare", "Winner declared & members notified")} data-testid={`declare-${election.id}`}><Trophy size={14} className="mr-1" /> Declare winner</Button>}
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {data.results.map((r) => (
          <div key={r.nomination_id} data-testid={`tally-${r.nomination_id}`}>
            <div className="flex justify-between text-sm"><span className="font-medium">{r.candidate}{data.winner?.candidate === r.candidate && !data.winner.tie && <Trophy size={13} className="ml-1 inline text-gold" />}</span><span className="font-mono-accent">{r.votes} · {r.pct}%</span></div>
            <div className="mt-1 h-2 rounded-full bg-white"><div className="h-2 rounded-full bg-crimson transition-all" style={{ width: `${(r.votes / max) * 100}%` }} /></div>
          </div>
        ))}
        {data.results.length === 0 && <p className="text-sm text-muted-foreground">No approved candidates.</p>}
      </div>
      {data.winner && <p className="mt-3 rounded-lg bg-gold-light p-2 text-sm font-semibold text-crimson" data-testid={`winner-${election.id}`}>{data.winner.tie ? "Tie — run-off required." : `${data.winner.candidate} declared elected as ${election.position}.`} <span className="text-xs font-normal">Declared by {data.winner.declared_by}.</span></p>}
    </div>
  );
}
